/**
 * Validated API Client
 *
 * Wrapper around axios client that adds runtime validation with Zod schemas.
 * Ensures all API responses are validated at runtime for type safety.
 *
 * Usage:
 * ```typescript
 * import { validatedGet, validatedPost } from '@/apis/validatedClient';
 * import { VendorDetailSchema } from '@/types/api/generated-schemas';
 *
 * // Runtime validated GET request
 * const vendor = await validatedGet('/vendors/1', VendorDetailSchema);
 *
 * // Runtime validated POST request
 * const newVendor = await validatedPost('/vendors', data, VendorDetailSchema);
 * ```
 */

import type { ZodSchema } from 'zod';
import type { AxiosRequestConfig } from 'axios';
import apiClient from './client';
import { validateResponse } from '@/lib/api/apiValidator';

/**
 * Validated GET request
 *
 * @param url - API endpoint URL
 * @param schema - Zod schema for response validation
 * @param config - Optional axios config
 * @returns Validated response data
 */
export async function validatedGet<T>(
  url: string,
  schema: ZodSchema<T>,
  config?: AxiosRequestConfig
): Promise<T> {
  const { data } = await apiClient.get(url, config);
  return validateResponse(data, schema, `GET ${url}`);
}

/**
 * Validated POST request
 *
 * @param url - API endpoint URL
 * @param requestData - Request body data
 * @param schema - Zod schema for response validation
 * @param config - Optional axios config
 * @returns Validated response data
 */
export async function validatedPost<T>(
  url: string,
  requestData: unknown,
  schema: ZodSchema<T>,
  config?: AxiosRequestConfig
): Promise<T> {
  const { data } = await apiClient.post(url, requestData, config);
  return validateResponse(data, schema, `POST ${url}`);
}

/**
 * Validated PUT request
 *
 * @param url - API endpoint URL
 * @param requestData - Request body data
 * @param schema - Zod schema for response validation
 * @param config - Optional axios config
 * @returns Validated response data
 */
export async function validatedPut<T>(
  url: string,
  requestData: unknown,
  schema: ZodSchema<T>,
  config?: AxiosRequestConfig
): Promise<T> {
  const { data } = await apiClient.put(url, requestData, config);
  return validateResponse(data, schema, `PUT ${url}`);
}

/**
 * Validated PATCH request
 *
 * @param url - API endpoint URL
 * @param requestData - Request body data
 * @param schema - Zod schema for response validation
 * @param config - Optional axios config
 * @returns Validated response data
 */
export async function validatedPatch<T>(
  url: string,
  requestData: unknown,
  schema: ZodSchema<T>,
  config?: AxiosRequestConfig
): Promise<T> {
  const { data } = await apiClient.patch(url, requestData, config);
  return validateResponse(data, schema, `PATCH ${url}`);
}

/**
 * Validated DELETE request
 *
 * @param url - API endpoint URL
 * @param schema - Zod schema for response validation (optional for delete)
 * @param config - Optional axios config
 * @returns Validated response data (if schema provided)
 */
export async function validatedDelete<T = void>(
  url: string,
  schema?: ZodSchema<T>,
  config?: AxiosRequestConfig
): Promise<T | void> {
  const { data } = await apiClient.delete(url, config);

  if (schema) {
    return validateResponse(data, schema, `DELETE ${url}`);
  }

  return data;
}
